/**
 * Shared PCO money derivations for the UI.
 *
 * These are display rules, not billing math — the server owns every cent
 * that reaches here. They live in one place because the PCO Log grid and the
 * PCO Detail stat band must show the same figure for the same PCO; when the
 * rule lived in both files, only one of them got fixed. `trancheIsFunded`
 * joined them for exactly that reason: the bucket classification had a copy
 * in EditPcoModal and another in PcoFundingSection.
 */

/** The inputs the Proposed figure reads. */
export interface ProposedInput {
  amount_proposed_cents: number | null;
  approved_total_cents: number;
  /** Sum of PENDING-classified buckets. Absent on older API responses. */
  pending_bucket_cents?: number;
}

/**
 * The "Proposed" figure (funding buckets, finding #13): the stored proposed
 * amount while it's set, and once "Edit details" retires it to null, the
 * bucket sum (approved + pending buckets) so the number isn't lost. Null when
 * there is nothing to show — callers render a dash or a blank cell, never 0,
 * because 0 would claim a priced scope worth nothing.
 */
export function proposedDisplayCents(p: ProposedInput): number | null {
  if (p.amount_proposed_cents !== null) return p.amount_proposed_cents;
  const fallback = p.approved_total_cents + (p.pending_bucket_cents ?? 0);
  return fallback !== 0 ? fallback : null;
}

/** The funding-bucket fields the Funded/Pending classification reads. */
export interface TrancheFundedInput {
  /**
   * The stored flag. Declared OPTIONAL here — and only here — because a
   * response can predate it; the shared `FundingTranche` contract keeps it
   * required, and a required boolean is assignable to this.
   */
  is_funded?: boolean;
  approved_date: string | null;
  mod_number: string | null;
  hoffman_fwa: string | null;
}

/**
 * Is this funding bucket Funded? Mirrors `isApprovedTranche` on the backend.
 *
 * DEPLOY-SKEW TOLERANT (2026-08-06). Funded is a stored flag as of migration
 * `0048`, but the web can be running against an API that predates it: Pages
 * deploys on push while the Worker is its own deploy, and local dev proxies
 * straight at the production API. On such a response `is_funded` is simply
 * absent — and read naively, `undefined` is falsy, so EVERY bucket on the
 * screen would read Pending and the PCO's Approved figure would collapse to
 * $0. The `??` arm is the pre-0048 rule (funded ⇔ any reference present), so
 * an old response degrades to exactly the behavior that shipped with it
 * rather than to a wrong number. Same fail-open shape as `pcoStatus.ts`'s
 * `funding_pending_cents` fallback.
 *
 * Absence is the ONLY trigger. A present `false` is a real answer — that is
 * the whole point of the change (the GC issues an FWA before the money
 * executes, so Pending-with-references is a legitimate bucket) and the
 * fallback must never second-guess it.
 *
 * The `??` arm is one of FOUR deliberate frozen copies of the dead pre-0048
 * rule; they are not to be unified — the map is in
 * `docs/system/04-pcos-and-funding.md`.
 *
 * DELETE THIS FALLBACK once no pre-0048 API can be reached — i.e. `0048` is
 * applied to prod, the Worker carrying `is_funded` is deployed, and no rollback
 * to an earlier Worker is on the table. Then this is `t.is_funded`, and
 * `TrancheFundedInput`'s three reference fields go with it: they exist ONLY to
 * feed the fallback, so at that moment they stop being requirements this
 * function has any business making of its callers.
 */
export function trancheIsFunded(t: TrancheFundedInput): boolean {
  return (
    t.is_funded ??
    (t.approved_date !== null || t.mod_number !== null || t.hoffman_fwa !== null)
  );
}

/**
 * The legacy proposal's residual, as a leading PENDING bucket — the SEED half
 * of "Edit details"' `amount_proposed_cents` bridge.
 *
 * `max(0, …)` mirrors `derivePerPco`'s `legacyGap` exactly, short-circuit
 * included: a null proposal has no proposal gap. Without that short-circuit a
 * PCO with no proposed figure and a NEGATIVE funded bucket (063, the −$2M
 * Central Laydown deduct) computes `max(0, 0 − (−2,000,000))` and seeds a
 * phantom +$2M pending bucket — which the next save writes to the database as
 * a real tranche. The PCO Log renderer carried the same defect against the
 * same PCO and was corrected the same way (2026-08-06).
 *
 * Consequence that the retirement below depends on: a NEGATIVE proposal always
 * residuals to 0. A deduct cannot be carried into a bucket by this math.
 *
 * ONE phantom shape survives, deliberately: a proposal of exactly **0** beside
 * a negative funded bucket still computes `max(0, 0 − (−2,000,000))`. It is
 * UNREACHABLE — no PCO on 25132 stores `amount_proposed_cents = 0` (queried
 * 2026-08-06) — and the fix does not belong here: this function mirrors
 * `derivePerPco`'s `legacyGap`, so the two would have to move together or the
 * editor would stop reproducing the figures the PCO Log prints. Same treatment
 * as the void-tranche gap in `04-pcos-and-funding.md`: named, not patched.
 */
export function legacyProposedResidualCents(
  amountProposedCents: number | null | undefined,
  approvedSumCents: number,
): number {
  if (amountProposedCents == null) return 0;
  return Math.max(0, amountProposedCents - approvedSumCents);
}

/** What a save writes to `amount_proposed_cents` — or omits. */
export type ProposedRetirementPatch = { amount_proposed_cents?: null };

/**
 * The `amount_proposed_cents` field of "Edit details"' PUT — the RETIREMENT
 * half of the same bridge, and the reason it is a function rather than the
 * literal `null` it used to be.
 *
 * The rule is unchanged where it was ever safe: a save retires the legacy
 * proposed figure to null, because pending money lives in the buckets now and
 * keeping both would double-count it. That is only true when the seed above
 * actually MIGRATED the proposal into a bucket. Two shapes where it did not,
 * and the field is therefore OMITTED so the server keeps what it stored:
 * `PUT /api/settings/pcos/:id` writes `body.amount_proposed_cents === undefined
 * ? existing : body.amount_proposed_cents == null ? null : Math.round(…)` — an
 * ABSENT field preserves, an explicit `null` retires. (`billing_type`
 * reaches the same outcome for omission by a DIFFERENT mechanism,
 * `body.billing_type ?? existing`, which preserves on null as well and so
 * cannot express a deliberate null at all. Money needs both, hence the
 * explicit `undefined` test here.)
 *
 *   - **A NEGATIVE proposal.** The residual is `max(0, …)`, so a deduct
 *     migrates into nothing and the retirement would destroy it outright. PCO
 *     063 held −$2,000,000 proposed beside its −$2,000,000 funded bucket;
 *     opening "Edit details" and pressing Save with no edits at all nulled the
 *     proposal in production on 2026-08-06, and it had to be restored by hand.
 *   - **A proposal the seed never READ.** It arrives on the settings-PCO list,
 *     a different payload from the tranches the seed waits on, so `undefined`
 *     means "not read yet" — never "already retired", which is what a stored
 *     null means. Retiring on that guess loses a POSITIVE proposal too, since
 *     the seed skipped its residual for the same reason. Callers must therefore
 *     pass **what the seed consumed**, never the current prop: the figure can
 *     land while the modal is open (`seededProposedRef` in `EditPcoModal`).
 */
export function proposedRetirementPatch(
  storedProposedCents: number | null | undefined,
): ProposedRetirementPatch {
  if (storedProposedCents === undefined) return {};
  if (storedProposedCents !== null && storedProposedCents < 0) return {};
  return { amount_proposed_cents: null };
}

/** Which stat leads the PCO Detail header's funding card. */
export type FundingCardLead = 'approved' | 'pending';

/**
 * The "064 silence" incident (2026-08-06 live review): a PM edited a bucket
 * and typed a MOD without flipping it to Funded, saved, and everything
 * persisted correctly as a Pending bucket carrying its reference — but the
 * header's Approved-funding-led card showed a bare dash, which read as data
 * loss on a PCO that plainly had money moving.
 *
 * Approved funding leads the funding card whenever there is one — it's the
 * budget ceiling, the more load-bearing of the two. But a PCO that is
 * genuinely all-Pending (nothing executed yet) has nothing to put in that
 * slot, so the card promotes Pending approval into the lead instead of
 * printing a dash over real money. Both zero is the true empty case — Approved
 * stays the lead, dash and all, same as it always has been.
 *
 * `approved` and `pending` are `PcoDetailPage`'s own local `approved` /
 * `pending` (`pcoMoney?.approved_total_cents` / `pcoMoney?.pending_cents`) —
 * the same figures the card already renders, so this makes no new claim about
 * the money and reads no new field.
 */
export function fundingCardLead(approvedCents: number, pendingCents: number): FundingCardLead {
  return approvedCents === 0 && pendingCents > 0 ? 'pending' : 'approved';
}
